import { useState } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import GlassCard from '@/effects/ui/GlassCard';
import { CheckSquare, Plus, Trash2, Circle, ChevronDown, ChevronRight, Luggage, Plane, Camera, FileText, CreditCard, Shield } from 'lucide-react';

const defaultCategories = [
  { icon: Luggage, title: '行李打包', color: '#7eccff', items: [
    { text: '身份证/护照', done: false },
    { text: '充电器/充电宝', done: false },
    { text: '换洗衣物', done: false },
    { text: '洗漱用品', done: false },
    { text: '常用药品', done: false },
  ]},
  { icon: Plane, title: '出行准备', color: '#a78bfa', items: [
    { text: '确认航班/车次', done: false },
    { text: '预定酒店', done: false },
    { text: '下载离线地图', done: false },
    { text: '购买旅行保险', done: false },
  ]},
  { icon: Shield, title: '出行检查', color: '#fbbf24', items: [
    { text: '关闭家中电源', done: false },
    { text: '锁好门窗', done: false },
    { text: '告知亲友行程', done: false },
    { text: '设置紧急联系人', done: false },
  ]},
  { icon: CreditCard, title: '财务准备', color: '#34d399', items: [
    { text: '准备少量现金', done: false },
    { text: '确认信用卡可用', done: false },
    { text: '备份支付密码', done: false },
  ]},
  { icon: Camera, title: '拍照清单', color: '#f472b6', items: [
    { text: '全家福', done: false },
    { text: '目的地地标', done: false },
    { text: '当地美食', done: false },
    { text: '自拍/合影', done: false },
  ]},
  { icon: FileText, title: '旅行收尾', color: '#60a5fa', items: [
    { text: '整理照片', done: false },
    { text: '写旅行日记', done: false },
    { text: '还信用卡', done: false },
    { text: '归还租借物品', done: false },
  ]},
];

export default function Checklist() {
  const [categories, setCategories] = useState(defaultCategories);
  const [collapsed, setCollapsed] = useState({});
  const [newItem, setNewItem] = useState('');
  const [activeCate, setActiveCate] = useState(null);

  const toggleItem = (catIdx, itemIdx) => {
    setCategories(prev => prev.map((cat, ci) =>
      ci === catIdx ? { ...cat, items: cat.items.map((it, ii) => ii === itemIdx ? { ...it, done: !it.done } : it) } : cat
    ));
  };

  const addItem = () => {
    if (!newItem.trim() || activeCate === null) return;
    setCategories(prev => prev.map((cat, ci) =>
      ci === activeCate ? { ...cat, items: [...cat.items, { text: newItem.trim(), done: false }] } : cat
    ));
    setNewItem('');
  };

  const removeItem = (catIdx, itemIdx) => {
    setCategories(prev => prev.map((cat, ci) =>
      ci === catIdx ? { ...cat, items: cat.items.filter((_, ii) => ii !== itemIdx) } : cat
    ));
  };

  const total = categories.reduce((s, c) => s + c.items.length, 0);
  const doneCount = categories.reduce((s, c) => s + c.items.filter(i => i.done).length, 0);
  const progress = total > 0 ? Math.round(doneCount / total * 100) : 0;

  return (
    <>
      <Navbar />
      <div className="relative pt-24 pb-16 px-4 max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold cb mb-2 flex items-center gap-3">
            <CheckSquare className="w-7 h-7" style={{ color: '#7eccff' }} />
            旅行待办清单
          </h1>
          <p className="cd text-base">出发前检查、行程中打卡、返程后整理，一次搞定</p>
        </div>

        {/* 进度条 */}
        <GlassCard className="p-5 mb-6" delay={0}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm cb font-medium">总进度</span>
            <span className="text-sm cd">{doneCount}/{total} 已完成</span>
          </div>
          <div className="h-3 rounded-full overflow-hidden" style={{ background: 'rgba(106,179,214,0.1)' }}>
            <div className="h-full rounded-full transition-all duration-500"
              style={{ width: `${progress}%`, background: 'linear-gradient(90deg, var(--deep), var(--glow), var(--accent))' }} />
          </div>
          <div className="text-center mt-2">
            <span className="text-2xl font-bold" style={{ color: progress === 100 ? '#34d399' : '#7eccff' }}>{progress}%</span>
            {progress === 100 && <span className="ml-2 text-sm" style={{ color: '#34d399' }}>准备就绪，可以出发！</span>}
          </div>
        </GlassCard>

        {/* 输入新条目 */}
        <GlassCard className="p-4 mb-6 flex items-center gap-3" delay={0.1}>
          <select value={activeCate ?? ''} onChange={e => setActiveCate(Number(e.target.value))}
            className="text-sm rounded-lg px-3 py-2 cb outline-none" style={{ background: 'rgba(106,179,214,0.08)', border: '1px solid rgba(156,200,224,0.1)' }}>
            <option value="">选择分类</option>
            {categories.map((c, i) => <option key={c.title} value={i}>{c.title}</option>)}
          </select>
          <input type="text" value={newItem} onChange={e => setNewItem(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addItem()}
            placeholder="添加新待办事项..."
            className="flex-1 text-sm cb px-3 py-2 rounded-lg outline-none" style={{ background: 'rgba(106,179,214,0.05)', border: '1px solid rgba(156,200,224,0.1)' }} />
          <button onClick={addItem} className="w-9 h-9 rounded-lg flex items-center justify-center text-white" style={{ background: 'linear-gradient(135deg, var(--glow), var(--mid))' }}>
            <Plus className="w-4 h-4" />
          </button>
        </GlassCard>

        {/* 分类列表 */}
        <div className="space-y-4">
          {categories.map((cat, catIdx) => {
            const Icon = cat.icon;
            const catTotal = cat.items.length;
            const catDone = cat.items.filter(i => i.done).length;
            const isOpen = collapsed[catIdx] !== false;
            return (
              <GlassCard key={cat.title} className="overflow-hidden p-0" delay={0.1 + catIdx * 0.05}>
                <button onClick={() => setCollapsed(p => ({ ...p, [catIdx]: !isOpen }))}
                  className="w-full flex items-center gap-3 p-4 text-left cb">
                  <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: cat.color + '22' }}>
                    <Icon className="w-4.5 h-4.5" style={{ color: cat.color }} />
                  </span>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{cat.title}</div>
                    <div className="text-xs cd mt-0.5">{catDone}/{catTotal} 已完成</div>
                  </div>
                  {isOpen ? <ChevronDown className="w-4 h-4 cd" /> : <ChevronRight className="w-4 h-4 cd" />}
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 space-y-1.5" style={{ borderTop: '1px solid rgba(156,200,224,0.06)' }}>
                    {cat.items.map((item, itemIdx) => (
                      <div key={itemIdx} className="flex items-center gap-3 group">
                        <button onClick={() => toggleItem(catIdx, itemIdx)} className="shrink-0">
                          {item.done
                            ? <CheckSquare className="w-4.5 h-4.5" style={{ color: '#34d399' }} />
                            : <Circle className="w-4.5 h-4.5 cd" />}
                        </button>
                        <span className={`flex-1 text-sm ${item.done ? 'line-through cd' : 'cb'}`}>{item.text}</span>
                        <button onClick={() => removeItem(catIdx, itemIdx)} className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </GlassCard>
            );
          })}
        </div>
      </div>
    </>
  );
}
